import React, { useCallback, useMemo } from 'react';
import {
  Hue,
  Alpha
} from 'react-color/lib/components/common';
import styled from 'styled-components';
import { CustomPicker } from 'react-color';
import { globalTheme } from '@clds/component-theme';
import { CustomSaturation } from '../CustomSaturation';
import { ColorInputControl } from './components/ColorInputControl';
import { convertToColorObj, getHexValueByAlpha } from './colorPickerUtils';

const SliderWrapper = styled.div`
  height: 10px;
  position: relative;
  margin-bottom: ${globalTheme.spacing.xxs};
`;

const SaturationWrapper = styled.div`
  height: 150px;
  width: 100%;
  position: relative;
  margin-bottom: ${globalTheme.spacing.xxs};
`;

const Swatch = styled.div`
  width: 24px;
  height: 24px;
  background: ${({ color: { r, g, b, a } }) => `rgba(${r}, ${g}, ${b}, ${a})`};
  margin-left: ${globalTheme.spacing.xxs};
  box-shadow: ${globalTheme.shadow.sm};
  border-radius: ${globalTheme.radius.md};
`;

const ColorPickerWrapper = styled.div`
  width: 200px;
  padding: ${globalTheme.spacing.xs} 0  ${globalTheme.spacing.xxs} 0;
  margin: 0 ${globalTheme.spacing.xs} 0 ${globalTheme.spacing.xs};
`;

export const ColorInputControlWrapper = styled.div`
  margin: ${globalTheme.spacing.xxs} ${globalTheme.spacing.sm} 0 ${globalTheme.spacing.sm};
`;

const SlidersControl = styled.div`
  display: flex;
`;

const SlidersWrapper = styled.div`
  width: calc(100% - 24px);
`;

/**
 * CustomPicker is react color controlled HOC that gets an initial color value and
 * provides a validated and formatted color object with all units (hex, rgb, hsl, hsv)
 * bound to onChange callback
 * @type {function(*): *}
 */
const CustomColorPicker = CustomPicker(({ hex, hsl, hsv, rgb, onChange, ...rest }) => {
  return (
    <ColorPickerWrapper data-test="color-picker" {...rest} >
      <SaturationWrapper>
        <CustomSaturation hsl={hsl} hsv={hsv} onChange={onChange} />
      </SaturationWrapper>
      <SlidersControl>
        <SlidersWrapper>
          <SliderWrapper>
            <Hue hsl={hsl} onChange={onChange} />
          </SliderWrapper>
          <SliderWrapper>
            <Alpha
              rgb={rgb} hsl={hsl}
              onChange={onChange}
            />
          </SliderWrapper>
        </SlidersWrapper>
        <Swatch color={rgb} />
      </SlidersControl>
      <ColorInputControlWrapper>
        <ColorInputControl onChange={onChange} color={{ hex, hsl, hsv, rgb }} />
      </ColorInputControlWrapper>
    </ColorPickerWrapper>
  );
});

/**
 * colorPicker adapter that receives color as HEX and send it back in the same format.
 * react-colors onChange cb is sending an object with all formatted units. We needed to extract a common ground unit
 * with hex representation (hsl) and send it back to the consumer with same format as received (hex).
 * @param color: string (hex 6/8 digits)
 * @param onChange: (color: string[]) => void (uppercase hex)
 * @param rest: {}
 * @returns {JSX.Element}
 * @constructor
 */
export const ColorPicker = ({ color, onChange, ...rest }) => {
  const convertedColor = useMemo(() => convertToColorObj(color).hsl, [color]);

  const handleChange =  useCallback((data) => {
    onChange?.(getHexValueByAlpha(data.hsl).toUpperCase());
  }, [onChange]);

  return <CustomColorPicker color={convertedColor} onChange={handleChange} {...rest}/>;
};
